import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { LanguageProvider } from './i18n/LanguageContext';
import GlassNav from './components/GlassNav';
import './App.css';

const Home = lazy(() => import('./Pages/Home'));
const Projects = lazy(() => import('./Pages/Projects'));
const Contact = lazy(() => import('./Pages/Contact'));
const Certifications = lazy(() => import('./Pages/Certifications'));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function useHashScroll() {
  const { hash, pathname } = useLocation();
  useEffect(() => {
    if (!hash) return undefined;
    const id = hash.replace('#', '');
    let attempts = 0;
    // The target page is lazy-loaded and mounts after the exit animation, so poll until it exists.
    const timer = setInterval(() => {
      const el = document.getElementById(id);
      attempts += 1;
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (el || attempts >= 30) clearInterval(timer);
    }, 100);
    return () => clearInterval(timer);
  }, [hash, pathname]);
}

function HashScroller() {
  useHashScroll();
  return null;
}

function Loading() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="w-8 h-8 border-2 border-ai-blue border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Suspense fallback={<Loading />} key={location.pathname}>
        <Routes location={location}>
          <Route path="/" element={<Home />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/certifications" element={<Certifications />} />
          {/* Redirects from old routes */}
          <Route path="/gamedeveloper" element={<Navigate to="/projects#university" replace />} />
          <Route path="/modeling" element={<Navigate to="/projects" replace />} />
          <Route path="/uxdesigner" element={<Navigate to="/projects" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
}

function App() {
  return (
    <LanguageProvider>
      <Router>
        <ScrollToTop />
        <HashScroller />
        <GlassNav />
        <main className="pt-0 md:pt-24 pb-24 md:pb-8">
          <AnimatedRoutes />
        </main>
      </Router>
    </LanguageProvider>
  );
}

export default App;
